import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { TranslatePipe } from '@ngx-translate/core';
import { UiButtonComponent } from '@ui';
import { OrganizationEffect } from '@effects/organization.effect';
import { OrganizationModels } from '@models/organization.model';
import { OrganizationsActions } from '@states/organization/organization.actions-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { EnableEscCloseDirective } from '../../shared/directives/enable-esc-close.directive';

@Component({
  selector: 'app-switch-organization-support-access-modal',
  standalone: true,
  imports: [NgIf, ReactiveFormsModule, TranslatePipe, UiButtonComponent],
  templateUrl: './switch-organization-support-access-modal.component.html',
  styleUrl: './switch-organization-support-access-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class SwitchOrganizationSupportAccessModalComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly organizationEffects = inject(OrganizationEffect);
  private readonly dialogRef = inject(MatDialogRef<SwitchOrganizationSupportAccessModalComponent>);
  private readonly destroyRef = inject(DestroyRef);

  public readonly isSwitching = this.store$.selectSignal(OrganizationSelectors.selectIsSwitchingBySupportAccess);

  public readonly supportAccessKeyControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: OrganizationModels.SwitchOrganizationSupportAccessModalData,
  ) {}

  public ngOnInit(): void {
    // The request that emails the access code has already succeeded by the time this modal
    // opens (see SwitchOrganizationSupportSearchComponent) — nothing to dispatch here.
    this.organizationEffects.switchOrganizationBySupportAccess$
      .pipe(
        ofType(OrganizationsActions.switchOrganizationBySupportAccessSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.dialogRef.close());
  }

  public onSwitch(): void {
    if (this.supportAccessKeyControl.invalid) {
      this.supportAccessKeyControl.markAsTouched();
      return;
    }

    const supportAccessKey = this.supportAccessKeyControl.value.trim();
    this.store$.dispatch(OrganizationsActions.switchOrganizationBySupportAccess({ supportAccessKey }));
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
